<template>
  <div>
    <div class="text-xl">{{ t("supervisorOrgSettings.apiKey.title") }}</div>
    <div v-if="supervisorOrg?.apiKeyLastChars && supervisorOrg?.apiKeyCreatedAt" class="mt-2">
      <p>
        {{ t("supervisorOrgSettings.apiKey.currentKey") }} ****{{ supervisorOrg.apiKeyLastChars }}
      </p>
      <p>
        {{ t("supervisorOrgSettings.apiKey.createdAt") }}
        {{ formatDate(supervisorOrg.apiKeyCreatedAt) }}
      </p>
      <v-btn
        class="mt-2"
        variant="outlined"
        color="warning"
        append-icon="mdi-refresh"
        :loading="generatingKey"
        :disabled="generatingKey"
        @click="generateApiKey()"
      >
        {{ t("supervisorOrgSettings.apiKey.regenerate") }}
      </v-btn>
    </div>
    <div v-else class="mt-2">
      <p>{{ t("supervisorOrgSettings.apiKey.noKey") }}</p>
      <v-btn
        class="mt-2"
        variant="outlined"
        color="primary"
        :loading="generatingKey"
        :disabled="generatingKey"
        @click="generateApiKey()"
      >
        {{ t("supervisorOrgSettings.apiKey.generate") }}
      </v-btn>
    </div>

    <v-dialog v-model="showApiKeyDialog" max-width="500">
      <v-card>
        <v-card-title>{{ t("supervisorOrgSettings.apiKey.newKeyTitle") }}</v-card-title>
        <v-card-text>
          <p>{{ t("supervisorOrgSettings.apiKey.newKeyInfo") }}</p>
          <v-text-field
            readonly
            :model-value="newApiKey"
            append-inner-icon="mdi-content-copy"
            class="mt-3"
            @click:append-inner="copyApiKeyToClipboard"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showApiKeyDialog = false">{{
            t("supervisorOrgSettings.apiKey.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="mt-8">
      <div class="text-xl">{{ t("supervisorOrgSettings.webhook.title") }}</div>

      <div class="mt-4">
        <v-btn class="mt-2" variant="outlined" color="primary" @click="configureWebhooks()">
          {{ t("supervisorOrgSettings.webhook.configureButton") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { useSupervisorOrg } from "@/composables/useSupervisorOrg";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { useUserStore } from "@/store/user";
import type { Timestamp } from "@firebase/firestore-types";
import { captureException } from "@sentry/vue";
import { ref } from "vue";

const user = useUserStore();
const generatingKey = ref(false);
const showApiKeyDialog = ref(false);
const newApiKey = ref("");

const { supervisorOrg } = useSupervisorOrg();
const { showSuccessToast, showErrorToast } = useToast();
const { t } = useI18n();
const openInNewTab = useOpenInNewTab();

const formatDate = (timestamp: Timestamp | undefined) => {
  return timestamp?.toDate().toLocaleDateString() || "";
};

const generateApiKey = async () => {
  generatingKey.value = true;
  try {
    console.log("Generating API key...");
    if (!user.supervisorOrgId) throw new Error("Missing supervisorOrgId");
    const key = await call("createApiKey")({ supervisorOrgId: user.supervisorOrgId });
    newApiKey.value = key;
    showApiKeyDialog.value = true;
  } catch (cause) {
    const error = new Error("Error generating API key", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("supervisorOrgSettings.apiKey.generateError"));
  } finally {
    generatingKey.value = false;
  }
};

const copyApiKeyToClipboard = async () => {
  if (newApiKey.value) {
    await navigator.clipboard.writeText(newApiKey.value);
    showSuccessToast(t("supervisorOrgSettings.apiKey.copiedToClipboard"));
  }
};

const configureWebhooks = () => {
  if (user.supervisorOrgId)
    openInNewTab("webhookDashboard", { supervisorOrgId: user.supervisorOrgId });
  else showErrorToast();
};
</script>
